import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { CsvButton, PrintButton } from "../../components/ReportButtons";

export default function PlanSalesTrip() {
  const { meta } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    state: "", country: "", segment: "", major_town: "", area_state: "",
    current_customer: "All", owns_equipment: "All", tier: "",
  });

  async function reload() {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "All"));
      const { data } = await api.get("/reports/plan-sales-trip", { params });
      setRows(data.rows);
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const csvParams = useMemo(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "All"));
    return { ...p, format: "csv" };
  }, [f]);

  return (
    <div className="space-y-4" data-testid="plan-trip-page">
      <div className="flex flex-wrap items-center justify-between gap-2 print-hide">
        <div>
          <h1 className="text-2xl font-bold">Plan Sales Trip</h1>
          <div className="text-sm text-gray-500">
            {rows.length} contact{rows.length !== 1 ? "s" : ""} match · pick a region + segment, run, then download.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CsvButton path="/reports/plan-sales-trip" params={csvParams} testId="trip-csv"/>
          <PrintButton testId="trip-print"/>
        </div>
      </div>

      {/* Filters */}
      <div className="cesco-card p-4 print-hide">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div><label className="cesco-label">State</label>
            <select className="cesco-input" value={f.state}
                    onChange={e => setF({ ...f, state: e.target.value })} data-testid="trip-state">
              <option value="">All states</option>
              {meta?.states?.map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label className="cesco-label">Major town</label>
            <input className="cesco-input" value={f.major_town}
                   onChange={e => setF({ ...f, major_town: e.target.value })}
                   placeholder="e.g. Toowoomba" data-testid="trip-town"/></div>
          <div><label className="cesco-label">Area state</label>
            <input className="cesco-input" value={f.area_state}
                   onChange={e => setF({ ...f, area_state: e.target.value })}
                   placeholder="e.g. North-QLD" data-testid="trip-area"/></div>
          <div><label className="cesco-label">Country</label>
            <input className="cesco-input" value={f.country}
                   onChange={e => setF({ ...f, country: e.target.value })}
                   placeholder="e.g. Australia" data-testid="trip-country"/></div>
          <div><label className="cesco-label">Segment</label>
            <select className="cesco-input" value={f.segment}
                    onChange={e => setF({ ...f, segment: e.target.value })} data-testid="trip-segment">
              <option value="">All segments</option>
              {meta?.product_segments?.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select></div>
          <div><label className="cesco-label">Tier</label>
            <select className="cesco-input" value={f.tier}
                    onChange={e => setF({ ...f, tier: e.target.value })} data-testid="trip-tier">
              <option value="">All tiers</option>
              {meta?.customer_tiers?.map(t => <option key={t.value} value={t.value}>{t.value}</option>)}
            </select></div>
          <div><label className="cesco-label">Current customer</label>
            <select className="cesco-input" value={f.current_customer}
                    onChange={e => setF({ ...f, current_customer: e.target.value })} data-testid="trip-current">
              <option value="All">All</option><option value="Yes">Yes</option><option value="No">No</option>
            </select></div>
          <div><label className="cesco-label">Owns our equipment</label>
            <select className="cesco-input" value={f.owns_equipment}
                    onChange={e => setF({ ...f, owns_equipment: e.target.value })} data-testid="trip-owns">
              <option value="All">All</option><option value="Yes">Yes</option><option value="No">No</option>
            </select></div>
        </div>
        <div className="mt-3"><button className="cesco-btn-primary" onClick={reload}
                                       data-testid="trip-apply">Apply filters</button></div>
      </div>

      {/* Data */}
      <div className="cesco-card overflow-x-auto print-region">
        <table className="w-full text-sm" data-testid="trip-table">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-left px-3 py-2">Town</th>
              <th className="text-left px-3 py-2">Company</th>
              <th className="text-left px-3 py-2">Tier</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">Mobile</th>
              <th className="text-left px-3 py-2">Segments</th>
              <th className="text-left px-3 py-2">Drill Market</th>
              <th className="text-left px-3 py-2">Last Meeting</th>
              <th className="text-left px-3 py-2">Customer</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="p-4 text-center text-gray-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="p-4 text-center text-gray-500">No matches - adjust filters</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.company_id || r.company_name}-${i}`} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{r.state}</td>
                <td className="px-3 py-2">{r.major_town || r.suburb}</td>
                <td className="px-3 py-2 font-medium">{r.company_name}</td>
                <td className="px-3 py-2">{r.customer_tier}</td>
                <td className="px-3 py-2">{r.contact_first_name} {r.contact_last_name}</td>
                <td className="px-3 py-2">{r.contact_mobile || "-"}</td>
                <td className="px-3 py-2 text-xs">{r.product_segments}</td>
                <td className="px-3 py-2 text-xs">{r.drill_market_segment || "-"}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{r.last_meeting_date || "-"}</td>
                <td className="px-3 py-2">{r.current_customer || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
